import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    IconButton,
    Typography,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Link,
    Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { useLoginFormDialogStore } from '@/store/loginFormDialogStore';
import { vendorSignIn } from '@/services/auth.service';
import { toast } from 'react-toastify';
import { ServiceResponseType } from '@/helpers/serviceParsing';
import { useNavigate, useParams } from 'react-router-dom';
import { useLoadingDialogStore } from '@/store/loadingDialogStore';
import VendorPasswordResetDialog from './VendorPasswordResetDialog';
import config from '@/config/config';

interface VendorLoginDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function VendorLoginDialog({ isOpen, onClose }: VendorLoginDialogProps) {
    const navigate = useNavigate();
    const params = useParams();
    const loginReferrer = useLoginFormDialogStore((state) => state.referrer);
    const { setIsOpen, setReferrer } = useLoginFormDialogStore((state) => state.action);
    const { setIsLoadingOpen } = useLoadingDialogStore((state) => state.action);
    const [keepLoggedIn, setKeepLoggedIn] = useState(false);
    const [passwordResetOpen, setPasswordResetOpen] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm();

    const handleClickClose = () => {
        setIsOpen(false);
        setReferrer('');
        reset({
            email: '',
            password: '',
        });
        onClose();
    };

    const handleClickLogin = () => {
        handleSubmit(async (data) => {
            try {
                setIsLoadingOpen(true);
                const { email, password } = data;
                await vendorSignIn({ email, password });
                if (!!loginReferrer) {
                    navigate(loginReferrer);
                }
                handleClickClose();
            } catch (error) {
                const { email } = data;
                const e = error as ServiceResponseType;
                if (e.status === 302) {
                    sessionStorage.setItem('userId', e.user_id);
                    sessionStorage.setItem('renewEmail', email);
                    toast.info('이메일 재인증이 필요합니다.');
                    handleClickClose();
                } else {
                    toast.error(e?.place_holder || '로그인 오류입니다. 해당 오류가 지속되면 관리자에게 문의해주세요.');
                }
            } finally {
                setIsLoadingOpen(false);
            }
        })();
    };

    const emailRegister = register('email', {
        required: '이메일을 입력해주세요.',
        pattern: {
            value: /^([0-9a-zA-Z_\.-]+)@([0-9a-zA-Z_-]+)(\.[0-9a-zA-Z_-]+){1,2}$/,
            message: '이메일 형식이 아닙니다.',
        },
    });

    const passwordRegister = register('password', {
        required: '비밀번호를 입력해주세요.',
        pattern: {
            value: /^[A-Za-z0-9!@#$%^&*?]{4,20}$/,
            message: '최소 4자 이상 20자이하 영어, 숫자, 특수문자 (!@#$%^&*?) 만 입력 가능',
        },
        minLength: {
            value: 4,
            message: '비밀번호는 최소 4글자 이상으로 입력 가능합니다.',
        },
        maxLength: {
            value: 20,
            message: '비밀번호는 최대 20글자 이하로 입력 가능합니다.',
        },
    });

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={handleClickClose}
                maxWidth="xs"
                fullWidth
                sx={{
                    zIndex: 1500,
                    '& .MuiDialog-paper': {
                        borderRadius: '12px !important',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    },
                }}
            >
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    {/* 닫기 버튼 */}
                    <IconButton
                        onClick={handleClickClose}
                        sx={{
                            position: 'absolute',
                            right: 12,
                            top: 12,
                            zIndex: 1,
                            color: '#000',
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Box sx={{ p: 4, pt: 6 }}>
                        {/* 로고 */}
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: 28,
                                    letterSpacing: '0.05em',
                                    background: 'linear-gradient(45deg, #FF4B6B 30%, #FF8E53 90%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    fontFamily: "'Montserrat', sans-serif",
                                    '&:hover': {
                                        transform: 'scale(1.05)',
                                        transition: 'transform 0.2s ease-in-out',
                                    },
                                }}
                            >
                                로그인
                            </Typography>
                        </Box>

                        {/* 입력 필드들 */}
                        <Box sx={{ mb: 3 }}>
                            <TextField
                                {...emailRegister}
                                placeholder="aroomy"
                                fullWidth
                                variant="outlined"
                                size="medium"
                                error={!!errors.email}
                                helperText={String(errors.email?.message || '')}
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#e0e8f7',
                                        border: '1px solid #000',
                                        borderRadius: '4px',
                                        '&:hover': {
                                            borderColor: '#000',
                                        },
                                        '&.Mui-focused': {
                                            borderColor: '#000',
                                            borderWidth: '1px',
                                        },
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: '#000',
                                        fontSize: '16px',
                                        padding: '12px 16px',
                                        '&:-webkit-autofill': {
                                            WebkitBoxShadow: '0 0 0 1000px #e0e8f7 inset',
                                            WebkitTextFillColor: '#000',
                                        },
                                        '&:-webkit-autofill:hover': {
                                            WebkitBoxShadow: '0 0 0 1000px #e0e8f7 inset',
                                        },
                                        '&:-webkit-autofill:focus': {
                                            WebkitBoxShadow: '0 0 0 1000px #e0e8f7 inset',
                                        },
                                    },
                                }}
                            />
                            <TextField
                                {...passwordRegister}
                                type="password"
                                placeholder="••••••••••••"
                                fullWidth
                                variant="outlined"
                                size="medium"
                                error={!!errors.password}
                                helperText={String(errors.password?.message || '')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#e0e8f7',
                                        border: '1px solid #000',
                                        borderRadius: '4px',
                                        '&:hover': {
                                            borderColor: '#000',
                                        },
                                        '&.Mui-focused': {
                                            borderColor: '#000',
                                            borderWidth: '1px',
                                        },
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: '#000',
                                        fontSize: '16px',
                                        padding: '12px 16px',
                                        '&:-webkit-autofill': {
                                            WebkitBoxShadow: '0 0 0 1000px #e0e8f7 inset',
                                            WebkitTextFillColor: '#000',
                                        },
                                        '&:-webkit-autofill:hover': {
                                            WebkitBoxShadow: '0 0 0 1000px #e0e8f7 inset',
                                        },
                                        '&:-webkit-autofill:focus': {
                                            WebkitBoxShadow: '0 0 0 1000px #e0e8f7 inset',
                                        },
                                    },
                                }}
                            />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* 로그인 버튼 */}
                        <Button
                            onClick={handleClickLogin}
                            fullWidth
                            variant="contained"
                            sx={{
                                mb: 2,
                                backgroundColor: '#FF4B6B',
                                color: '#fff',
                                py: 1.5,
                                fontSize: '16px',
                                fontWeight: 600,
                                borderRadius: '8px',
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: '#E03E5A',
                                },
                            }}
                        >
                            로그인 하기
                        </Button>

                        {/* 문의하기 버튼 */}
                        <Button
                            onClick={() => {
                                const termsUrl = `${config.url.matching_url}/terms`;
                                window.open(termsUrl, '_blank');
                                handleClickClose();
                            }}
                            fullWidth
                            variant="outlined"
                            sx={{
                                mb: 4,
                                border: '1px solid #000',
                                color: '#000',
                                py: 1.5,
                                fontSize: '16px',
                                fontWeight: 600,
                                borderRadius: '8px',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: '#FF4B6B',
                                    color: '#FF4B6B',
                                },
                            }}
                        >
                            회원가입
                        </Button>

                        {/* 도움말 링크 */}
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography sx={{ color: '#666', fontSize: '14px', mb: 1 }}>
                                계정이 기억나지 않으세요?
                            </Typography>
                            <Link
                                onClick={() => {
                                    setPasswordResetOpen(true);
                                    handleClickClose();
                                }}
                                sx={{
                                    color: '#666',
                                    fontSize: '14px',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: '#FF4B6B',
                                    },
                                }}
                            >
                                비밀번호 찾기
                            </Link>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
            <VendorPasswordResetDialog open={passwordResetOpen} onClose={() => setPasswordResetOpen(false)} />
        </>
    );
}
